import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import type { KVActiveLayer, KVTemperature } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { getDataLayersVisibility } from 'features/kestrelViewer/utils/visibilityUtils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type GeckoKestrelModel, type KVDataLayerConfig } from 'types'

const DEFAULT_TEMPERATURE_MIN = -100
const DEFAULT_TEMPERATURE_MAX = 300
const DEFAULT_LAYER_OPACITY = 1.0

const isValidLayerType = (type: string): type is 'rgb' | 'visual' | 'raw' => {
  return type === 'rgb' || type === 'visual' || type === 'raw'
}

const emitLayerCommand = (
  pixelStreaming: PixelStreaming,
  layer: KVActiveLayer,
  isMultiObjectModel: boolean
) => {
  if (layer.type === 'rgb') {
    pixelStreaming.emitUIInteraction({
      command: KestrelCommands.SHOW_LAYER_RGB,
      prefix: layer.prefix,
      opacity: layer.opacity || DEFAULT_LAYER_OPACITY,
      use_multi_obj_model: isMultiObjectModel,
    })
  } else if (layer.type === 'visual') {
    pixelStreaming.emitUIInteraction({
      command: KestrelCommands.SHOW_LAYER_VISUAL,
      prefix: layer.prefix,
      opacity: layer.opacity || DEFAULT_LAYER_OPACITY,
      use_multi_obj_model: isMultiObjectModel,
    })
  } else if (layer.type === 'raw') {
    const decodeMin = layer.decode_min_f ?? DEFAULT_TEMPERATURE_MIN
    const decodeMax = layer.decode_max_f ?? DEFAULT_TEMPERATURE_MAX
    pixelStreaming.emitUIInteraction({
      command: KestrelCommands.SHOW_LAYER_RAW,
      prefix: layer.prefix,
      decode_min_f: decodeMin,
      decode_max_f: decodeMax,
      visible_min_f: layer.visible_min_f ?? decodeMin,
      visible_max_f: layer.visible_max_f ?? decodeMax,
      opacity: layer.opacity || DEFAULT_LAYER_OPACITY,
      gradient: layer.gradient,
      use_multi_obj_model: isMultiObjectModel,
    })
  }
}

interface UseDataLayerManagementProps {
  kestrelModel: GeckoKestrelModel | undefined
  pixelStreaming?: PixelStreaming
}

export const useDataLayerManagement = ({
  kestrelModel,
  pixelStreaming,
}: UseDataLayerManagementProps) => {
  const [activeLayer, setActiveLayer] = useState<KVActiveLayer>({
    prefix: 'RGB',
    type: 'rgb',
    opacity: 1.0,
  })
  const [availableLayerPrefixes, setAvailableLayerPrefixes] = useState<
    string[]
  >(['RGB'])
  const [currentTemperature, setCurrentTemperature] =
    useState<KVTemperature | null>(null)

  const activeLayerRef = useRef<KVActiveLayer>(activeLayer)

  useEffect(() => {
    activeLayerRef.current = activeLayer
  }, [activeLayer])

  // Initialize layer data from kestrel model
  useEffect(() => {
    if (!getDataLayersVisibility(kestrelModel) || !kestrelModel?.data) return

    const config = kestrelModel.data.dataLayersConfig
    const prefixes = config.getAvailablePrefixes()

    setAvailableLayerPrefixes(prefixes)

    // Only initialize if activeLayer is still the default RGB
    if (activeLayer.prefix === 'RGB' && activeLayer.type === 'rgb') {
      const firstLayer = config.getFirstLayer()
      setActiveLayer({
        prefix: firstLayer.prefix,
        type: firstLayer.type,
        opacity: 1.0,
        decode_min_f: firstLayer.decodeRange?.min_f,
        decode_max_f: firstLayer.decodeRange?.max_f,
        visible_min_f: firstLayer.decodeRange?.min_f,
        visible_max_f: firstLayer.decodeRange?.max_f,
        isCustom: firstLayer.isCustom,
        customId: firstLayer.customId,
      })
    }
  }, [kestrelModel, activeLayer.prefix, activeLayer.type])

  // Handle layer switching via keyboard
  const handleLayerSwitch = useCallback(
    (event: KeyboardEvent) => {
      if (!pixelStreaming) return

      if (
        event.key.toLowerCase() === 'k' &&
        getDataLayersVisibility(kestrelModel) &&
        kestrelModel?.data?.dataLayersConfig?.layerSwitchingEnabled
      ) {
        event.preventDefault()

        const config = kestrelModel?.data?.dataLayersConfig
        if (!config) return

        const currentLayerIndex = config.layers.findIndex(
          (layer: KVDataLayerConfig) =>
            layer.prefix === activeLayer.prefix &&
            layer.type === activeLayer.type
        )

        if (currentLayerIndex === -1 || config.layers.length === 0) {
          return
        }

        const nextLayerIndex = (currentLayerIndex + 1) % config.layers.length
        const nextLayerConfig = config.layers[nextLayerIndex]

        if (!isValidLayerType(nextLayerConfig.type)) {
          return
        }

        const newLayer: KVActiveLayer = {
          ...activeLayer,
          prefix: nextLayerConfig.prefix,
          type: nextLayerConfig.type,
          decode_min_f: nextLayerConfig.decodeRange?.min_f,
          decode_max_f: nextLayerConfig.decodeRange?.max_f,
          visible_min_f:
            nextLayerConfig.type === 'raw'
              ? nextLayerConfig.decodeRange?.min_f
              : undefined,
          visible_max_f:
            nextLayerConfig.type === 'raw'
              ? nextLayerConfig.decodeRange?.max_f
              : undefined,
          isCustom: nextLayerConfig.isCustom,
          customId: nextLayerConfig.customId,
        }
        setActiveLayer(newLayer)

        const isMultiObjectModel =
          kestrelModel?.data?.dataLayersConfig?.isMultiObjectModel ?? false
        emitLayerCommand(pixelStreaming, newLayer, isMultiObjectModel)
      }
    },
    [pixelStreaming, activeLayer, kestrelModel]
  )

  // Emit layer commands to pixel streaming
  const emitCurrentLayerCommand = useCallback(() => {
    if (!pixelStreaming) return
    const isMultiObjectModel =
      kestrelModel?.data?.dataLayersConfig?.isMultiObjectModel ?? false
    emitLayerCommand(pixelStreaming, activeLayerRef.current, isMultiObjectModel)
  }, [pixelStreaming, kestrelModel?.data?.dataLayersConfig?.isMultiObjectModel])

  return {
    activeLayer,
    setActiveLayer,
    availableLayerPrefixes,
    setAvailableLayerPrefixes,
    currentTemperature,
    setCurrentTemperature,
    handleLayerSwitch,
    emitCurrentLayerCommand,
    hasDataLayersSupport: getDataLayersVisibility(kestrelModel),
  }
}
